import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';
import { validateEmail } from '@utils/validators';

import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  onboardingFormEmailEdit,
  onboardingFormSubmitEmail,
  Recipient,
  SubmitError,
} from './api/requests';

type Props = ModalProps<{
  recipient?: Recipient;
}>;

export const NotifyEmailModal = (props: Props) => {
  const { closeModal, recipient } = props;
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();

  const [hasPressedSend, setHasPressedSend] = useState(false);
  const [name, setName] = useState(recipient?.name ?? '');
  const [email, setEmail] = useState(recipient?.email ?? '');

  const noName = !Boolean(name);
  const isInvalidEmail = !validateEmail(email);
  const isEdit = recipient !== undefined;

  const hasChanged = isEdit ? recipient?.name !== name || recipient?.email !== email : true;

  const handleSave = () => {
    setHasPressedSend(true);
    if (isInvalidEmail || noName) {
      return;
    }
    isEdit && recipient ? editRecipient.mutate(recipient.id) : addRecipient.mutate();
  };

  const queryClient = useQueryClient();

  const addRecipient = useMutation(() => onboardingFormSubmitEmail(name, email), {
    onSuccess: () => {
      queryClient.invalidateQueries(['notify-email-data']);
      showSuccess(t('integrations.onboardingform.settings.add.modal.recipient.added.toast'));
      closeModal();
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });

  const editRecipient = useMutation(
    (recipientId: number) => onboardingFormEmailEdit(recipientId, name, email),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notify-email-data']);
        showSuccess(t('integrations.onboardingform.settings.edit.modal.recipient.changed.toast'));
        closeModal();
      },
    },
  );

  return (
    <Modal>
      <Stack space={4}>
        <Stack orientation="horizontal" space={2} justify="space-between">
          <Text
            font="heading1"
            color="purple100"
            text={
              isEdit
                ? t('integrations.onboardingform.settings.edit.modal.title')
                : t('integrations.onboardingform.settings.add.modal.title')
            }
          />
          <IconButton variant="ghost-purple" onClick={closeModal} icon={<IconCloseRegular />} />
        </Stack>
        <Stack space={4}>
          <TextField
            label={t('integrations.onboardingform.settings.add.modal.name')}
            onChange={setName}
            defaultValue={name}
            error={
              hasPressedSend &&
              noName &&
              t('integrations.onboardingform.settings.add.modal.name.missing')
            }
          />
          <TextField
            label={t('integrations.onboardingform.settings.add.modal.email')}
            onChange={setEmail}
            defaultValue={email}
            error={
              hasPressedSend &&
              isInvalidEmail &&
              t('integrations.onboardingform.settings.emails.error')
            }
          />
        </Stack>
      </Stack>
      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          isDisabled={addRecipient.isLoading || editRecipient.isLoading}
          text={t('modals.confirm.button.cancel')}
        />
        <Button
          variant="primary-blue"
          onClick={handleSave}
          isDisabled={addRecipient.isLoading || (isEdit && !hasChanged) || editRecipient.isLoading}
          text={t('common.forms.button.save')}
        />
      </Modal.Actions>
    </Modal>
  );
};
